import React, { FC, useEffect, useState } from 'react';
import { Backdrop, Box, Modal, Slider } from '@mui/material';
import { useGetAccount, useGetNetworkConfig, useGetIsLoggedIn } from '@multiversx/sdk-dapp/hooks';
import BigNumber from '@multiversx/sdk-dapp/node_modules/bignumber.js';
import { styled } from '@mui/material/styles';
import { InputNumber } from 'antd';
import Dropdown from 'react-bootstrap/Dropdown';
import egld_white_logo from 'assets/img/EGLD_white.svg';
import './index.scss';
import { animated, useSpring } from 'react-spring';
import { JewelFarmBaseContext, JewelFarmContext, JewelLendPool, NftWithIndexType } from 'z/types';
import { ASH_LP_DECIMALS, convertBigNumberValueToLocalString, convertEsdtToWei, convertWeiToEsdt, ERROR_CONNECT_YOUR_WALLET, getTokenDecimal, getTokenImage, getTokenTicker, MAX_PERCENTAGE, parseBigNumber, toastError, YEAR_IN_SECONDS } from 'z/utils';
import { AUTO_COMPOUND_CRON_JOB_TIME, contracts, isDev, JEWEL_ASHSWAP_SC_ADDRESS, MAX_LEVERAGE, SLIPPAGE } from 'config';
import { enterFarm, wrapEgld } from 'z/elrond/jewel-farm';
import { isMobile } from 'react-device-detect';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { getNftsByCollectionFromApi, getTokenPriceFromAshswap } from 'z/api';
import { farmAbiRegistry } from 'z/elrond';
import { BinaryCodec } from '@multiversx/sdk-core/out';
import { ContractManager, ChainId, MVXProxyNetworkAddress } from '@ashswap/ash-sdk-js/out';
import { ProxyNetworkProvider } from '@multiversx/sdk-network-providers/out';
import PoolContract from '@ashswap/ash-sdk-js/out/helper/contracts/pool';

const fadeBoxStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    minWidth: 300,
    // bgcolor: '#2b3943',
    boxShadow: 24,
    px: 3,
    py: 4,
    borderRadius: '10px',
    color: 'white',
    background: 'linear-gradient(180deg, #2b3943, #1a242b)',
};

interface FadeProps {
    children?: React.ReactElement;
    in: boolean;
    onEnter?: () => void;
    onExited?: () => void;
}

const Fade = React.forwardRef<HTMLDivElement, FadeProps>(function Fade(props, ref) {
    const { in: open, children, onEnter, onExited, ...other } = props;
    const style = useSpring({
        from: { opacity: 0 },
        to: { opacity: open ? 1 : 0 },
        onStart: () => {
            if (open && onEnter) {
                onEnter();
            }
        },
        onRest: () => {
            if (!open && onExited) {
                onExited();
            }
        },
    });

    return (
        <animated.div ref={ref} style={style} {...other}>
            {children}
        </animated.div>
    );
});

const LeverageSlider = styled(Slider)({
    color: '#54F5B7',
    height: 4,
    '& .MuiSlider-track': {
        border: 'none',
    },
    '& .MuiSlider-markLabel': {
        top: '30px'
    },
    '& .MuiSlider-thumb': {
        height: 15,
        width: 15,
        backgroundColor: 'currentColor',
        border: '2px solid currentColor',
        '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
            boxShadow: 'inherit',
        },
        '&:before': {
            display: 'none',
        },
    },
    '& .MuiSlider-valueLabel': {
        lineHeight: 1,
        fontSize: 10,
        background: 'unset',
        padding: 0,
        width: 35,
        height: 35,
        color: 'black',
        borderRadius: '50% 50% 50% 0',
        backgroundColor: '#54F5B7',
        transformOrigin: 'bottom left',
        transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
        '&:before': { display: 'none' },
        '&.MuiSlider-valueLabelOpen': {
            transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
        },
        '& > *': {
            transform: 'rotate(45deg)',
        },
    },
});

const valueLabelFormat = (value: number) => {
    if (value === 1) {
        return `${value} X`;
    }
    return `${value} X`;
};

interface valueMark {
    value: number,
    label: string,
}

interface FarmPoolProps {
    commonSettings: JewelFarmBaseContext | undefined,
    farmPool: JewelFarmContext,
    lendPools: JewelLendPool[],
}

export const FarmPool: FC<FarmPoolProps> = ({
    commonSettings,
    farmPool,
    lendPools,
}) => {

    const {
        network: { apiAddress },
        chainID,
    } = useGetNetworkConfig();
    const { address, balance, shard } = useGetAccount();
    const isLoggedIn = useGetIsLoggedIn();

    const [selectedLeverage, setSelectedLeverage] = useState<number>(1);
    const [showFarmModal, setShowFarmModal] = useState<boolean>(false);
    const [selectedCurrency, setSelectedCurrency] = useState<string | undefined>(farmPool.token_ids[0]);
    const [leverage, setLeverage] = useState<number>(1);
    const [tokenAmounts, setTokenAmounts] = useState<string[]>([]);
    const [minDepositAmounts, setMinDepositAmounts] = useState<string[]>([]);
    const [maxMark, setMaxMark] = useState<number>(1);
    const [amountMarks, setAmountMarks] = useState<valueMark[]>([
        {
            value: 1,
            label: '1',
        },
    ]);
    const [debtAmount, setDebtAmount] = useState<string>('0');
    const [currentDepositAmount, setCurrentDepositAmount] = useState<string>('0');

    const [stakedLpAmount, setStakedLpAmount] = useState<string>('0');
    const [lpTokenPrice, setLpTokenPrice] = useState<string>('0');
    const [tokenPrices, setTokenPrices] = useState([]);
    const [reserves, setReserves] = useState<string[]>([]);
    const [lpPercent, setLpPercent] = useState<string>('0');
    const [apy, setApy] = useState<number>(0);

    useEffect(() => {
        if (!commonSettings) return;

        const _tokens = commonSettings.ashswap_tokens;
        if (_tokens.data) {
            setLpTokenPrice(new BigNumber(farmPool.staked_lp_amount).multipliedBy(farmPool.lp_price).toFixed());
            setTokenPrices(_tokens.data.tokens);
        }
        setStakedLpAmount(farmPool.staked_lp_amount);
        setReserves(farmPool.reserves);
        setLpPercent(new BigNumber(farmPool.lp_total_supply).dividedBy(farmPool.staked_lp_amount).toFixed());
        setApy(farmPool.apy);
    }, [commonSettings]);

    useEffect(() => {
        if (!commonSettings || !selectedCurrency) return;

        const _minDepositAmounts = [];
        for (let i = 0; i < farmPool.token_ids.length; i++) {
            for (let j = 0; j < commonSettings.min_deposit_tokens.length; j++) {
                if (farmPool.token_ids[i] === commonSettings.min_deposit_tokens[j]) {
                    _minDepositAmounts.push(new BigNumber(commonSettings.min_deposit_values[j]).dividedBy(10).plus(commonSettings.min_deposit_values[j]).toFixed());
                    break;
                }
            }
        }
        setMinDepositAmounts(_minDepositAmounts);

        // check max leverage
        let _liquidationThresholds = 0;
        for (let i = 0; i < commonSettings.liquidation_tokens.length; i++) {
            if (commonSettings.liquidation_tokens[i] === selectedCurrency) {
                _liquidationThresholds = commonSettings.liquidation_thresholds[i];
                break;
            }
        }
        if (_liquidationThresholds > 0) {
            const percent = 100 * MAX_PERCENTAGE;
            let maxLeverage = Math.floor((percent / (percent - _liquidationThresholds) * 100) / 100);
            maxLeverage = Math.floor(maxLeverage - maxLeverage / 10);
            if (maxLeverage > MAX_LEVERAGE) {
                maxLeverage = MAX_LEVERAGE;
            }

            if (maxLeverage > 1) {
                const labels = [];
                let i = 1;
                for (i = 1; i <= maxLeverage; i += 0.5) {
                    labels.push({
                        value: i,
                        label: i.toString(),
                    });
                }
                setAmountMarks(labels);
                setMaxMark(i - 0.5);
                if (leverage > maxLeverage) {
                    setLeverage(maxLeverage);
                }
            }
        }

    }, [commonSettings, selectedCurrency]);

    useEffect(() => {
        (async () => {
            if (selectedCurrency && leverage > 1) {
                await updateDebtAmount(selectedCurrency, tokenAmounts);
            }
        })();
    }, [leverage, selectedCurrency]);

    const updateDebtAmount = async (borrowTokenId: string, amounts: string[]) => {
        let _depositAmount = new BigNumber('0');
        for (let i = 0; i < farmPool.token_ids.length; i++) {
            if (farmPool.token_ids[i] === borrowTokenId) {
                _depositAmount = _depositAmount.plus(convertEsdtToWei(amounts[i], getTokenDecimal(borrowTokenId)));
            } else {
                if (new BigNumber(amounts[i]).gt(0)) {
                    const outputAmount = await getOutputAmount(farmPool.token_ids[i], borrowTokenId);
                    _depositAmount = new BigNumber(amounts[i]).multipliedBy(outputAmount).plus(_depositAmount);
                }
            }
        }

        setCurrentDepositAmount(_depositAmount.toFixed());
        if (_depositAmount.gt('0') && leverage > 1) {
            const _debtAmount = _depositAmount.multipliedBy(leverage - 1);
            setDebtAmount(_debtAmount.toFixed());
        }
    };

    const getOutputAmount = async (tokenIn: string, tokenOut: string) => {
        const proxy = new ProxyNetworkProvider(isDev ? MVXProxyNetworkAddress.Devnet : MVXProxyNetworkAddress.Mainnet);
        const poolContract = ContractManager.getPoolV2Contract(
            farmPool.dex_pool_address
        ).onChain(isDev ? ChainId.Devnet : ChainId.Mainnet).onProxy(proxy);
        const amountIn = 1;
        let firstTokenIndex = 0;
        let secondTokenIndex = 1;
        for (let i = 0; i < farmPool.token_ids.length; i++) {
            if (farmPool.token_ids[i] === tokenIn) {
                firstTokenIndex = i;
            }
            if (farmPool.token_ids[i] === tokenOut) {
                secondTokenIndex = i;
            }
        }
        const { fee, outputAmount } = await poolContract.estimateAmountOut(
            firstTokenIndex,
            secondTokenIndex,
            new BigNumber(convertEsdtToWei(amountIn, getTokenDecimal(tokenIn))), // amount_in
        );

        return outputAmount.toFixed();
    };

    const handleShowModal = () => {
        if (!isLoggedIn) {
            toastError(ERROR_CONNECT_YOUR_WALLET);
            return;
        }
        setLeverage(1);
        setTokenAmounts(['0', '0']);
        setShowFarmModal(true);
        setCurrentDepositAmount('0');
        setDebtAmount('0');
    };

    const handleSelectedCurrency = async (value: string) => {
        setSelectedCurrency(value);
    };

    // useEffect(() => {
    //     if (!commonSettings) return;
    //     const amounts: string[] = [];
    //     for (let i = 0; i < farmPool.token_ids.length; i++) {
    //         if (tokenAmounts.length > i) {
    //             amounts[i] = tokenAmounts[i];
    //         } else {
    //             amounts[i] = '0';
    //         }
    //     }
    //     setTokenAmounts(amounts);
    // }, [commonSettings]);

    const handleOnChange = async (index: number, value: string) => {
        const amounts: string[] = [];
        for (let i = 0; i < tokenAmounts.length; i++) {
            if (index === i) {
                amounts.push(value);
            } else {
                amounts.push(tokenAmounts[i]);
            }
        }
        setTokenAmounts(amounts);
        if (selectedCurrency) {
            await updateDebtAmount(selectedCurrency, amounts);
        }
    };

    const handleMax = async (index: number) => {
        if (!commonSettings) return;
        const amounts: string[] = [];
        for (let i = 0; i < tokenAmounts.length; i++) {
            if (index === i) {
                amounts.push(convertWeiToEsdt(commonSettings.user_tokens[farmPool.token_ids[i]].balance, getTokenDecimal(farmPool.token_ids[index])).toFixed());
            } else {
                amounts.push(tokenAmounts[i]);
            }
        }
        setTokenAmounts(amounts);
        if (selectedCurrency) {
            await updateDebtAmount(selectedCurrency, amounts);
        }
    };

    const handleEnterFarm = async () => {
        if (!commonSettings || !isLoggedIn || !selectedCurrency) return;

        let availableAmount = '0';
        for (let i = 0; i < lendPools.length; i++) {
            if (lendPools[i].deposit_token_id === selectedCurrency) {
                availableAmount = new BigNumber(lendPools[i].deposit_token_amount).minus(lendPools[i].loan_amount).toFixed();
                break;
            }
        }

        const tokenIds: string[] = [];
        const amounts: string[] = [];
        let _minDepositAmount = '0';
        // let depositAmount = BigNumber('0');
        for (let i = 0; i < tokenAmounts.length; i++) {
            if (new BigNumber(tokenAmounts[i]).gt(0)) {
                tokenIds.push(farmPool.token_ids[i]);
                const value = convertEsdtToWei(tokenAmounts[i], getTokenDecimal(farmPool.token_ids[i])).toFixed();
                amounts.push(value);

                // get amount by borrow token
                // if (farmPool.token_ids[i] === selectedCurrency) {
                //     depositAmount = depositAmount.plus(value);
                // } else {
                //     const outputAmount = await getOutputAmount(farmPool.token_ids[i], selectedCurrency);
                //     depositAmount = BigNumber(tokenAmounts[i]).multipliedBy(outputAmount).plus(depositAmount);
                // }
            }
            if (farmPool.token_ids[i] === selectedCurrency) {
                _minDepositAmount = minDepositAmounts[i];
            }
        }

        if (new BigNumber(currentDepositAmount).lte(_minDepositAmount)) {
            toastError(`Please deposit more than ${convertBigNumberValueToLocalString(convertWeiToEsdt(_minDepositAmount, getTokenDecimal(selectedCurrency)))} ${getTokenTicker(selectedCurrency)}`);
            return;
        } else {

            if (leverage > 1 && (new BigNumber(availableAmount).eq('0') || new BigNumber(debtAmount).eq('0') || new BigNumber(availableAmount).lt(debtAmount))) {
                toastError('Not enough liquidity in lend pool');
                return;
            }


            await enterFarm(chainID, JEWEL_ASHSWAP_SC_ADDRESS, address, farmPool.farm_id, tokenIds, amounts, Math.round(leverage * 100) * MAX_PERCENTAGE, SLIPPAGE * MAX_PERCENTAGE, selectedCurrency);

            setShowFarmModal(false);
        }
    };

    const handleWrap = async () => {
        if (!isLoggedIn || new BigNumber(balance).lte(convertEsdtToWei(1, 18))) return;

        let wrapAddress = contracts.Wrap0.address;

        if (shard === 1) {
            wrapAddress = contracts.Wrap1.address;
        } else if (shard === 2) {
            wrapAddress = contracts.Wrap2.address;
        }

        await wrapEgld(chainID, wrapAddress, address, new BigNumber(balance).minus(convertEsdtToWei(1, 18)).toFixed());
    };

    return (
        <>
            {isMobile ? (
                <div className='row mt-4 farm-container mobile-farm-container'>
                    <div className='col-lg-4 col-md-4 col-sm-4 align-self-center d-flex mt-2'>
                        <div className='d-flex justify-content-center align-items-center'>
                            <div className='combine-bage'>
                                {farmPool.token_ids.map((item: string, index: number) => (<img key={index} src={getTokenImage(item)} className='egld-image mx-2' alt={item} />))}
                            </div>
                            <div>
                                <div>{farmPool.token_ids.map((item: string, key: number) => {
                                    let poolName = '';
                                    if (key === (farmPool.token_ids.length - 1)) {
                                        poolName += getTokenTicker(item);
                                    } else {
                                        poolName += `${getTokenTicker(item)}-`;
                                    }
                                    return poolName;
                                })}</div>
                                <div>AshSwap</div>
                            </div>
                        </div>
                    </div>
                    <div className='col-lg-2 col-md-2 col-sm-2 d-flex justify-content-between mt-4'>
                        <div className='d-flex align-items-center'>TVL</div>
                        <div>
                            <div className='text-end'>
                                {`$${convertBigNumberValueToLocalString(convertWeiToEsdt(lpTokenPrice, ASH_LP_DECIMALS))} (${convertBigNumberValueToLocalString(convertWeiToEsdt(stakedLpAmount, ASH_LP_DECIMALS))} LP tokens)`}
                            </div>
                            {
                                farmPool.token_ids.map((item: string, index: number) => {
                                    return <div key={index} className='text-end'>
                                        {`${reserves.length > 0 ? convertBigNumberValueToLocalString(convertWeiToEsdt(new BigNumber(reserves[index]).dividedBy(lpPercent), getTokenDecimal(item))) : '0'} ${getTokenTicker(item)} (1 ${getTokenTicker(item)} = ${tokenPrices.length > 0 ? tokenPrices.filter((token: any) => token.id === item)[0]['price'] : '-'} USD)`}
                                    </div>;
                                })
                            }
                        </div>
                    </div>
                    <div className='col-lg-1 col-md-1 col-sm-1 d-flex justify-content-between mt-2'>
                        <div className='d-flex'>
                            <div className='d-flex'>APY</div>
                            <Tooltip
                                placement="top"
                                title="Based on APY of the current underlying farming pools. It does not include fees from occasional rebalancing"
                            >
                                <InfoCircleOutlined className='h6 mx-1' style={{ marginTop: '1px', cursor: 'pointer' }} />
                            </Tooltip>
                        </div>
                        <div className='avaliable-color'>{`${(apy * selectedLeverage).toFixed(2)} %`}</div>
                    </div>
                    <div className='col-lg-2 col-md-2 col-sm-2 d-flex justify-content-between mt-2'>
                        <div className='d-flex align-items-center'>Leverage</div>
                        <InputNumber
                            className={`farm-leverage-input ${isMobile ? 'text-center' : 'px-3'}`}
                            type="number"
                            min={1}
                            max={3}
                            step={0.5}
                            value={selectedLeverage}
                            onChange={(value: any) => setSelectedLeverage(value)}
                        />
                    </div>
                    <div className='col-lg-3 col-md-3 col-sm-3 align-self-center mt-4'>
                        <div className='row'>
                            <div className='col-12 d-flex justify-content-center'>
                                <div className="eg-btn btn--primary2 capsule px-4 py-2 w-100" style={{ cursor: 'pointer' }} onClick={handleShowModal}>
                                    Farm
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className='row mt-4 farm-container'>
                    <div className='col-lg-4 col-md-4 col-sm-4 align-self-center'>
                        <div className='d-flex align-items-center'>
                            <div className='combine-bage'>
                                {farmPool.token_ids.map((item: string, index: number) => (<img key={index} src={getTokenImage(item)} className='egld-image mx-2' alt={item} />))}
                            </div>
                            <div>
                                <div>{farmPool.token_ids.map((item: string, index: number) => {
                                    let poolName = '';
                                    if (index === (farmPool.token_ids.length - 1)) {
                                        poolName += getTokenTicker(item);
                                    } else {
                                        poolName += `${getTokenTicker(item)}-`;
                                    }
                                    return poolName;
                                })}</div>
                                <div>AshSwap</div>
                            </div>
                        </div>
                    </div>
                    <div className='col-lg-3 col-md-3 col-sm-3 text-center align-self-center'>
                        <div className='d-flex justify-content-center'>
                            {`$${convertBigNumberValueToLocalString(convertWeiToEsdt(lpTokenPrice, ASH_LP_DECIMALS))} (${convertBigNumberValueToLocalString(convertWeiToEsdt(stakedLpAmount, ASH_LP_DECIMALS))} LP tokens)`}
                        </div>
                        {
                            farmPool.token_ids.map((item: string, index: number) => {
                                return <div key={index} className='d-flex justify-content-center'>
                                    {`${reserves.length > 0 ? convertBigNumberValueToLocalString(convertWeiToEsdt(new BigNumber(reserves[index]).dividedBy(lpPercent), getTokenDecimal(item))) : '0'} ${getTokenTicker(item)} (1 ${getTokenTicker(item)} = ${tokenPrices.length > 0 ? tokenPrices.filter((token: any) => token.id === item)[0]['price'] : '-'} USD)`}
                                </div>;
                            })
                        }
                    </div>
                    <div className='col-lg-1 col-md-1 col-sm-1 text-center align-self-center'>
                        <div className='avaliable-color'>{`${(apy * selectedLeverage).toFixed(2)} %`}</div>
                    </div>
                    <div className='col-lg-2 col-md-2 col-sm-2 text-center align-self-center'>
                        <InputNumber
                            className={`farm-leverage-input ${isMobile ? 'text-center' : 'px-3'}`}
                            type="number"
                            min={1}
                            max={3}
                            step={0.5}
                            value={selectedLeverage}
                            onChange={(value: any) => setSelectedLeverage(value)}
                        />
                    </div>
                    <div className='col-lg-2 col-md-2 col-sm-2 align-self-center'>
                        <div className='row'>
                            <div className='col-12 d-flex justify-content-center'>
                                <div className="eg-btn btn--primary2 capsule px-4 py-2" style={{ cursor: 'pointer' }} onClick={handleShowModal}>
                                    Farm
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                open={showFarmModal}
                onClose={() => setShowFarmModal(false)}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={showFarmModal}>
                    <Box sx={fadeBoxStyle} className='farm-modal scrollbar' style={{ width: 'auto', maxHeight: '95%', overflowY: 'scroll' }}>
                        <div className='d-flex'>
                            Warning: Please be aware of a potential large price impact when opening a large position. This is due to your position size compared to the underlying liquidity of the pool.
                        </div>
                        <div className='d-flex mt-4'>
                            How much would you like to add for farming?
                        </div>
                        {farmPool.token_ids.map((item: string, index: number) => (
                            <div className='mt-2 borrow-form' key={index}>
                                <div className='d-flex justify-content-between align-items-center'>
                                    <div className='currency-item'>
                                        <img src={getTokenImage(item)} alt={item} />
                                        <div>{getTokenTicker(item)}</div>
                                    </div>
                                    <input type="number" value={tokenAmounts[index]} onChange={(e: any) => handleOnChange(index, e.target.value)} />
                                </div>
                                <div className='d-flex justify-content-between align-items-center'>
                                    <div>
                                        <div className='d-flex'>
                                            <div className='d-flex align-items-center' style={{ fontSize: '0.85rem', marginRight: '3px' }}>{`Available :`}</div>
                                            <div>
                                                <div style={{ fontSize: '0.85rem' }}>{`${commonSettings ? convertBigNumberValueToLocalString(convertWeiToEsdt(commonSettings.user_tokens[item]?.balance, getTokenDecimal(item))) : '-'} ${getTokenTicker(item)}`}</div>
                                            </div>
                                        </div>
                                        {/* {getTokenTicker(item) === 'WEGLD' && convertWeiToEsdt(balance).gt(1) && (
                                            <div className='d-flex'>
                                                <div className='d-flex align-items-center' style={{ fontSize: '0.85rem', marginRight: '3px' }}>
                                                    <button className='max-but' onClick={handleWrap}>WRAP</button>
                                                </div>
                                                <div className='d-flex'>
                                                    <div className='d-flex align-items-center' style={{ fontSize: '0.85rem', marginRight: '3px' }}>{`: ${convertBigNumberValueToLocalString(convertWeiToEsdt(balance))} EGLD`}</div>
                                                </div>
                                            </div>
                                        )} */}
                                    </div>
                                    {/* <span style={{ fontSize: '0.85rem' }}>{`Available : ${commonSettings ? convertBigNumberValueToLocalString(convertWeiToEsdt(commonSettings.user_tokens[item]?.balance, getTokenDecimal(item))) : '-'} ${getTokenTicker(item)}`}</span> */}
                                    <button className='max-but' onClick={() => handleMax(index)}>MAX</button>
                                </div>
                            </div>
                        ))}
                        <div className='d-flex mt-4 gap-2'>
                            <div className='d-flex align-items-center'>Leverage</div>
                            <InputNumber
                                className={`farm-leverage-input ${isMobile ? 'text-center' : 'px-3'}`}
                                type="number"
                                min={1}
                                max={maxMark}
                                step={0.5}
                                value={leverage}
                                onChange={(value: any) => setLeverage(value)}
                            />
                        </div>
                        <div className='d-flex justify-content-center'>
                            <div style={{ width: '95%' }}>
                                <LeverageSlider
                                    valueLabelDisplay="auto"
                                    aria-label="pretto slider"
                                    min={1}
                                    max={maxMark}
                                    step={0.01}
                                    marks={amountMarks}
                                    value={leverage}
                                    valueLabelFormat={valueLabelFormat}
                                    onChange={(e: any) => setLeverage(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className='d-flex mt-1'>
                            Which asset would you like to borrow?
                        </div>
                        <div className='d-flex mt-2'>
                            <Dropdown className='select-borrow-currency-dropdown'>
                                <Dropdown.Toggle id="dropdown-basic-button" className='currency-item justify-content-between'>
                                    <div>
                                        <img src={selectedCurrency ? getTokenImage(selectedCurrency) : egld_white_logo} />
                                        <div>{selectedCurrency ? getTokenTicker(selectedCurrency) : 'EGLD'}</div>
                                    </div>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {farmPool.token_ids.map((item: string, index: number) => (
                                        <Dropdown.Item className='currency-item' onClick={() => handleSelectedCurrency(item)} key={index}>
                                            <img src={getTokenImage(item)} alt={item}></img>
                                            <div>{getTokenTicker(item)}</div>
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                        <div className='d-flex mt-4'>
                            Summary
                        </div>
                        <div className='farm-summary mt-2'>
                            <div className='w-100 d-flex justify-content-between'>
                                <span>Leverage</span>
                                <div className='d-flex gap-2'>
                                    <span>{`${leverage}X`}</span>
                                </div>
                            </div>
                            <div className='w-100 d-flex justify-content-between mt-2'>
                                <span>Yield Farm APY</span>
                                <div className='d-flex gap-2'>
                                    <span>{`${(apy * leverage).toFixed(2)} %`}</span>
                                </div>
                            </div>
                            <div className='w-100 d-flex justify-content-between mt-2'>
                                <span>Deposit Amount (MIN)</span>
                                <div className='d-flex gap-1'>
                                    {minDepositAmounts.length === farmPool.token_ids.length && farmPool.token_ids.map((item: string, index: number) => (
                                        <div className='d-flex gap-1' key={index}>
                                            <img className='egld-badge' src={getTokenImage(item)} alt={item} />
                                            <span>{`${convertBigNumberValueToLocalString(convertWeiToEsdt(minDepositAmounts[index], getTokenDecimal(item)))} ${farmPool.token_ids.length === (index + 1) ? '' : 'or'}`}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className='w-100 d-flex justify-content-between align-items-center mt-2'>
                                <span>Deposit Amount</span>
                                <div className='d-flex gap-1'>
                                    {farmPool.token_ids.map((item: string, index: number) => (
                                        <div className='d-flex gap-1' key={index}>
                                            <img className='egld-badge' src={getTokenImage(item)} alt={item} />
                                            <span>{`${convertBigNumberValueToLocalString(tokenAmounts[index], 0)} ${farmPool.token_ids.length === (index + 1) ? '' : '+'}`}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {selectedCurrency && (
                                <div className='w-100 d-flex justify-content-between mt-2'>
                                    <span>Debt {getTokenTicker(selectedCurrency)}</span>
                                    <div className='d-flex gap-2'>
                                        <img className='egld-badge' src={getTokenImage(selectedCurrency)} alt={selectedCurrency} />
                                        <span>{convertBigNumberValueToLocalString(convertWeiToEsdt(debtAmount, getTokenDecimal(selectedCurrency)))}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className='d-flex mt-4'>
                            Please keep in mind that when you leverage above 2x, you will have a slight short on the borrowed asset. The other paired asset will have typical long exposure, so choose which asset you borrow wisely.
                        </div>
                        <div className='d-flex justify-content-center mt-4'>
                            <div className="eg-btn btn--primary2 capsule px-4 py-2" style={{ cursor: 'pointer' }} onClick={handleEnterFarm}>
                                Farm
                            </div>
                        </div>
                    </Box>
                </Fade>
            </Modal>
        </>
    );
};
